import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "aa-cookie-consent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
    } catch (e) {
      /* ignore */
    }
  }, []);

  // Expose banner height as a CSS variable so fixed elements (e.g. nav)
  // can offset themselves below it.
  useEffect(() => {
    const root = document.documentElement;
    if (!visible) {
      root.style.setProperty("--cookie-banner-h", "0px");
      return;
    }
    const update = () => {
      const h = ref.current ? ref.current.offsetHeight : 0;
      root.style.setProperty("--cookie-banner-h", `${h}px`);
    };
    update();
    let ro;
    if (ref.current && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(ref.current);
    }
    window.addEventListener("resize", update);
    return () => {
      ro && ro.disconnect();
      window.removeEventListener("resize", update);
      root.style.setProperty("--cookie-banner-h", "0px");
    };
  }, [visible]);

  const remember = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      /* ignore */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      ref={ref}
      data-testid="cookie-banner"
      className="fixed top-0 inset-x-0 z-50 bg-[#2C2825] text-[#F3EFEA] shadow-lg"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Cookie className="h-5 w-5 text-[#C9BFB5] flex-shrink-0 mt-0.5" />
          <p className="text-sm leading-relaxed text-[#E6DFD7]">
            Vi använder cookies för att förbättra din upplevelse och för
            marknadsföring.{" "}
            <Link
              to="/integritetspolicy"
              className="underline underline-offset-4 hover:text-white"
              data-testid="cookie-policy-link"
            >
              Läs mer i vår integritetspolicy
            </Link>
            .
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => remember("declined")}
            data-testid="cookie-decline"
            className="text-xs tracking-[0.2em] uppercase px-4 py-2.5 rounded-full border border-[#5C5651] text-[#E6DFD7] hover:border-[#C9BFB5] hover:text-white transition-colors"
          >
            Neka
          </button>
          <button
            type="button"
            onClick={() => remember("accepted")}
            data-testid="cookie-accept"
            className="text-xs tracking-[0.2em] uppercase px-5 py-2.5 rounded-full bg-[#A98C78] hover:bg-[#8C7362] text-white transition-colors"
          >
            Acceptera
          </button>
          <button
            type="button"
            onClick={() => remember("dismissed")}
            data-testid="cookie-close"
            aria-label="Stäng"
            className="p-2 text-[#C9BFB5] hover:text-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
